import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Clock3, Gem } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'

const formatFund = (value) => Math.max(0, Number(value) || 0).toLocaleString('en-US')

const formatCountdown = (value) => {
  const total = Math.max(0, Math.floor(Number(value) || 0))
  const hours = String(Math.floor(total / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0')
  const seconds = String(total % 60).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

/**
 * Вход в банды — блок на главной и в своём профиле.
 * Всегда промо каталога, независимо от членства: клик ведёт на `/teams`,
 * где участник увидит карточку своей банды. Фонд и таймер суточного розыгрыша
 * берутся из `me.team_top` уже загруженного ответа `/api/me/`.
 */
const TeamBanner = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const teamTop = useAppStore((state) => state.user?.team_top)
  const { playSound } = useAudioManager({ select: 'select' })
  const [deadline] = useState(() => Date.now() + Math.max(0, Number(teamTop?.ends_in) || 0) * 1000)
  const [remaining, setRemaining] = useState(() => Math.max(0, Number(teamTop?.ends_in) || 0))

  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)))
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [deadline])

  const open = (path) => {
    playSound('select')
    navigate(path)
  }

  return (
    <button
      type="button"
      onClick={() => open('/teams')}
      className="flex w-full flex-col overflow-hidden rounded-2xl border border-accent-primary/30 bg-surface text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] transition-all active:scale-[0.98] hover:border-accent-primary/50"
    >
      <div className="relative">
        <img
          src="/images/teams-banner.jpg"
          alt={t('team.bannerTitle', 'Банды')}
          className="aspect-[16/9] w-full object-cover"
          loading="lazy"
        />
        {teamTop && (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" aria-hidden="true" />
            <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-1.5 p-3">
              <span className="inline-flex items-center gap-1 rounded-lg border border-accent-primary/40 bg-dark-primary/70 px-2 py-1 text-[11px] font-bold tabular-nums text-accent-primary backdrop-blur-sm">
                <Gem size={12} className="shrink-0" />
                {formatFund(teamTop.fund)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-lg border border-violet-400/40 bg-dark-primary/70 px-2 py-1 text-[11px] font-bold text-violet-200 backdrop-blur-sm">
                <Clock3 size={12} className="shrink-0 text-violet-300" />
                <span className="font-mono tabular-nums">{formatCountdown(remaining)}</span>
              </span>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-3 p-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-accent-primary">
            {t('team.bannerTitle', 'Банды')}
          </p>
          <p className="mt-2 text-sm font-semibold text-white">
            {t('team.bannerDescription', 'Играй в команде — вместе увеличивайте шансы на победу и получайте награды в общую казну')}
          </p>
        </div>
        <ChevronRight size={16} className="shrink-0 text-accent-primary" />
      </div>
    </button>
  )
}

export default TeamBanner
